import React, { useEffect, useState, useRef } from 'react';
/**
 * `WorldPay` integrates the Worldpay embedded HPP (hosted payment page)
 * Javascript library.
 *
 * Flow summary:
 * - Injects the Worldpay integration script on mount.
 * - Calls `PaymentApi.sendOrder` once the script is loaded to obtain an HPP URL.
 * - Configures the Worldpay library (`WPCL.Library().setup(...)`) to render an
 *   iframe into the `custom-html` target.
 * - Receives payment results through `resultCallback` and reports success/failure
 *   to the parent via `onPaymentSuccess` / `onPaymentError`.
 * - Supports follow-up transaction actions via `PaymentApi.updateWorldPayPayment`
 *   when `voidTransaction` or `captureTransaction` flags are toggled.
 */

import { PaymentApi } from '../../../api/PaymentApi';
import {
  CardPayment,
  OrderInquiryResponse,
  PaymentSuccessResponse
} from '../CommonTypes';
import LoadingContainer from '../LoadingContainer/LoadingContainer';
import { useTranslation } from 'react-i18next';
import { CountryIsoCodes } from '../Constants';
import { CommonValidation } from '../CommonValidation/commonValidation';

type WorldPayProps = {
  token: string;
  isCallCentreUser: boolean;
  payment: CardPayment;
  registeringPolicy: boolean;
  voidTransaction: boolean;
  captureTransaction: boolean;
  policyNumbers: string;
  onPaymentSuccess: (
    transactionId: string,
    bankAuthorisationCode: string
  ) => void;
  onPaymentVoided: () => void;
  onPaymentCaptured: () => void;
  countryCode: string;
  onPaymentError: () => void;
  brandName: string;
};

type Gateway = {
  paymentAmount?: string;
  paymentCurrency?: string;
  mac?: string;
  paymentStatus?: string;
  orderKey?: string;
};

type Error = {
  referenceNumber: string;
};

type Order = {
  orderKey: string;
  status: string;
  //error: Error;
  //gateway: Gateway;
};

type ResultCallbackData = {
  order: Order;
  error: Error;
  gateway: Gateway;
};

type FlowCallbackData = {
  page: string;
};

type CustomOptions = {
  url: string;
  type: string;
  inject?: string;
  target?: string;
  accessibility: boolean;
  debug: boolean;
  language: string;
  country: string;
  flowCallback: (data: FlowCallbackData) => void;
  resultCallback: (data: ResultCallbackData) => void;
};

type CardInfo = {
  cardNumber: string;
  cardHolderName: string;
  cardExpiryMonth: number;
  cardExpiryYear: number;
  cardSecurityCode: string;
};

type libraryObject = {
  setup: (customOptions: CustomOptions) => void;
  populate: (cardInfo: CardInfo) => void;
};

const WorldPay = ({
  token,
  isCallCentreUser,
  payment,
  registeringPolicy,
  voidTransaction,
  captureTransaction,
  policyNumbers,
  onPaymentSuccess,
  onPaymentVoided,
  onPaymentCaptured,
  countryCode,
  onPaymentError,
  brandName
}: WorldPayProps): JSX.Element => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [optionsSet, setOptionsSet] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const { t } = useTranslation();
  const libraryRef = useRef<libraryObject>();

  useEffect(() => {
    const loadError = () => {
      setScriptError(true);
      console.log('WorldPay script failed to load');
    };

    const scriptUpdate = () => {
      setScriptLoaded(true);
      console.log('WorldPay script loaded');
    };

    const LoadExternalScript = () => {
      const externalScript = document.createElement('script');
      externalScript.id = 'worldPay';
      externalScript.onerror = loadError;
      externalScript.async = true;
      externalScript.type = 'text/javascript';
      externalScript.setAttribute('crossorigin', 'anonymous');
      externalScript.onload = scriptUpdate;
      document.head.appendChild(externalScript);
      externalScript.src =
        'https://payments.worldpay.com/resources/hpp/integrations/embedded/js/hpp-embedded-integration-library.js';
    };

    LoadExternalScript();
  }, []);

  useEffect(() => {
    if (scriptLoaded === false) {
      return;
    }

    const controller = new AbortController();

    const callback = (data: PaymentSuccessResponse) => {
      console.log(`Sending order success: ${JSON.stringify(data.acsUrl)}`);
      if (data.acsUrl) {
        setPaymentUrl(data.acsUrl);
        setOrderNo(data.transactionId);
      } else {
        setOptionsSet(true);
        setScriptError(true);
      }
    };

    const failureCallback = () => {
      console.log('Sending order failed');
    };

    const api = new PaymentApi();
    api.sendOrder(
      token,
      payment,
      isCallCentreUser,
      callback,
      failureCallback,
      countryCode,
      getLanguage(),
      controller.signal
    );

    return () => {
      controller.abort();
    };
  }, [scriptLoaded]);

  useEffect(() => {
    if (paymentUrl !== '' && optionsSet === false) {
      const customOptions: CustomOptions = {
        url: paymentUrl,
        type: 'iframe',
        target: 'custom-html',
        inject: 'immediate',
        accessibility: true,
        debug: true,
        language: getLanguage(),
        country: countryCode,
        flowCallback: flowCallback,
        resultCallback: resultCallback
      };

      //@ts-ignore
      libraryRef.current = new WPCL.Library();
      libraryRef.current?.setup(customOptions);

      console.log('WorldPay setup done');

      setOptionsSet(true);
    }
  }, [paymentUrl]);

  useEffect(() => {
    const controller = new AbortController();

    if (voidTransaction === true) {
      const callback = () => {
        onPaymentVoided();
      };

      const failureCallback = () => {
        onPaymentVoided();
      };

      new PaymentApi().updateWorldPayPayment(
        token,
        orderNo,
        'void',
        isCallCentreUser,
        payment.amount,
        policyNumbers,
        payment.firstName + ' ' + payment.surname,
        payment.email,
        callback,
        failureCallback,
        countryCode,
        controller.signal
      );
    }

    return () => {
      controller.abort();
    };
  }, [voidTransaction]);

  useEffect(() => {
    const controller = new AbortController();

    if (captureTransaction === true) {
      const callback = () => {
        onPaymentCaptured();
      };

      const failureCallback = () => {
        onPaymentCaptured();
      };

      new PaymentApi().updateWorldPayPayment(
        token,
        orderNo,
        'capture',
        isCallCentreUser,
        payment.amount,
        policyNumbers,
        payment.firstName + ' ' + payment.surname,
        payment.email,
        callback,
        failureCallback,
        countryCode,
        controller.signal
      );
    }

    return () => {
      controller.abort();
    };
  }, [captureTransaction]);

  const pollTimeoutRef = useRef<number | null>(null);
  const pollIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!orderNo) return;

    let disposed = false;
    let inFlight = false;
    const controller = new AbortController();

    const pollOnce = () => {
      if (disposed || inFlight) return;
      inFlight = true;

      const inquirySuccessCallback = (response: OrderInquiryResponse) => {
        inFlight = false;
        if (response.isError) {
          submitPaymentResponse('ERROR', response.status ?? '');
        } else {
          submitPaymentResponse(response.status ?? '', '');
        }
        console.log(`Order ${orderNo} status: ${response.status}`);

        // act on final result
        if (['AUTHORISED', 'CAPTURED'].includes(response.status)) {
          // stop polling
          if (pollIntervalRef.current)
            window.clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;

          onPaymentSuccess(orderNo, orderNo);
        }
      };

      const inquiryFailureCallback = () => {
        console.log('Order inquiry failed');
        inFlight = false;
      };

      new PaymentApi().getOrderInquiry(
        orderNo,
        isCallCentreUser,
        brandName,
        countryCode,
        token,
        inquirySuccessCallback,
        inquiryFailureCallback,
        controller.signal
      );
    };

    // start 5 minutes after order was sent
    pollTimeoutRef.current = window.setTimeout(() => {
      pollOnce(); // first poll at T+5m
      pollIntervalRef.current = window.setInterval(pollOnce, 30_000); // then every 30s
    }, 5 * 60_000);

    return () => {
      disposed = true;
      controller.abort();

      if (pollTimeoutRef.current) window.clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;

      if (pollIntervalRef.current)
        window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    };
  }, [orderNo, token]);

  const getLanguage = () => {
    if (countryCode === CountryIsoCodes.AT) {
      return CountryIsoCodes.DE;
    } else {
      return countryCode;
    }
  };

  const flowCallback = (result: FlowCallbackData) => {
    console.log(`flowCallback: ${JSON.stringify(result)}`);
  };

  const resultCallback = (responseData: ResultCallbackData) => {
    const status = responseData.order?.status ?? '';

    submitPaymentResponse(status, responseData.error?.referenceNumber ?? '');

    if (status === 'success') {
      onPaymentSuccess(orderNo, orderNo);
    } else if (
      !responseData?.order ||
      CommonValidation.isEmpty(status) ||
      CommonValidation.isNotEmpty(responseData.error?.referenceNumber)
    ) {
      onPaymentError();
    }
  };

  const submitPaymentResponse = (status: string, error: string) => {
    const controller = new AbortController();

    const api = new PaymentApi();
    api.paymentResponse(
      token,
      orderNo,
      status,
      error,
      payment.email,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  if (scriptError === true) {
    return <>ERROR</>;
  }

  return (
    <>
      <LoadingContainer
        loading={!optionsSet}
        message={t('customer-single-payment.loading')}></LoadingContainer>
      <LoadingContainer
        loading={registeringPolicy}
        message={t(
          'customer-single-payment.register-policy'
        )}></LoadingContainer>
      <div id='custom-html'></div>
    </>
  );
};

export default WorldPay;
